import { Search } from 'lucide-react'
import { useMemo, useState } from 'react'

import { ThatchTooltip } from 'components/reusable/Tooltip'
import { Pagination, Table } from 'slab'
import { convertGrapeMoneyToNumber, formatMoney } from 'utils'
import { getNextMonthName, getPriorMonthName } from 'utils/billing'
import { type Money } from 'utils/web_api.schemas'

import { useInvoice } from './InvoiceContext'
import { MemberInvoiceBreakdownMobile } from './mobile'

type SortColumn = 'full_name' | 'plan_year' | 'prefunding' | 'premiums' | 'coverage_corrections' | 'other' | 'total'
type SortDirection = 'asc' | 'desc' | false

interface MemberInvoiceBreakdownProps {
  onMemberClick: (memberInvoiceId: string) => void
}

function formatMoneyValue (money: Money | undefined): string {
  const value = convertGrapeMoneyToNumber(money)
  if (value === 0) {
    return '---'
  }
  return formatMoney(value)
}

export function MemberInvoiceBreakdown ({ onMemberClick }: MemberInvoiceBreakdownProps) {
  const { invoice, enrichedMemberInvoices } = useInvoice()
  const [searchTerm, setSearchTerm] = useState('')
  const [sortColumn, setSortColumn] = useState<SortColumn>('full_name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [currentPage, setCurrentPage] = useState(0)
  const rowsPerPage = 20

  const nextMonthName = getNextMonthName(invoice.period_end_date)
  const priorMonthName = getPriorMonthName(invoice.period_start_date)
  const showPlanYear = (invoice.plan_years?.length ?? 0) > 1

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return enrichedMemberInvoices
    }
    const lowerSearch = searchTerm.toLowerCase()
    return enrichedMemberInvoices.filter((mi) =>
      (mi.full_name || '').toLowerCase().includes(lowerSearch)
    )
  }, [enrichedMemberInvoices, searchTerm])

  const sortedData = useMemo(() => {
    if (!sortDirection) {
      return filteredData
    }

    const sorted = [...filteredData]
    sorted.sort((a, b) => {
      let aValue: string | number
      let bValue: string | number

      switch (sortColumn) {
        case 'full_name':
          aValue = a.full_name || ''
          bValue = b.full_name || ''
          break
        case 'plan_year':
          aValue = a.plan_year ?? 0
          bValue = b.plan_year ?? 0
          break
        case 'prefunding':
          aValue = convertGrapeMoneyToNumber(a.amounts?.total_insurance_premiums)
          bValue = convertGrapeMoneyToNumber(b.amounts?.total_insurance_premiums)
          break
        case 'premiums':
          aValue = convertGrapeMoneyToNumber(a.amounts?.premiums)
          bValue = convertGrapeMoneyToNumber(b.amounts?.premiums)
          break
        case 'coverage_corrections':
          aValue = convertGrapeMoneyToNumber(a.amounts?.coverage_corrections)
          bValue = convertGrapeMoneyToNumber(b.amounts?.coverage_corrections)
          break
        case 'other':
          aValue = convertGrapeMoneyToNumber(a.amounts?.other)
          bValue = convertGrapeMoneyToNumber(b.amounts?.other)
          break
        case 'total':
          aValue = convertGrapeMoneyToNumber(a.amounts?.total)
          bValue = convertGrapeMoneyToNumber(b.amounts?.total)
          break
        default:
          return 0
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
      }

      return 0
    })
    return sorted
  }, [filteredData, sortColumn, sortDirection])

  const paginatedData = useMemo(() => {
    const startIndex = currentPage * rowsPerPage
    return sortedData.slice(startIndex, startIndex + rowsPerPage)
  }, [sortedData, currentPage, rowsPerPage])

  const totalPages = Math.ceil(sortedData.length / rowsPerPage)

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else if (sortDirection === 'desc') {
        setSortDirection(false)
        setSortColumn('full_name')
      } else {
        setSortDirection('asc')
      }
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  const getSortState = (column: SortColumn): SortDirection => {
    return sortColumn === column ? sortDirection : false
  }

  return (
    <div className='p-6'>
      <h4 className='body-bold'>Member healthcare costs</h4>

      <div className='mt-6'>
        <div className='relative'>
          <div className='absolute inset-y-0 left-0 flex items-center pl-3'>
            <Search size={16} className='text-gray-400' />
          </div>
          <input
            placeholder='Find a member'
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setCurrentPage(0)
            }}
            className='w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-thatch-500 focus:border-thatch-500'
          />
        </div>
      </div>

      <div className='mt-3'>
        <ThatchTooltip text="Why am I being billed for more than employees' allowances?">
          <button
            type='button'
            className='inline-flex items-center justify-center rounded-full bg-white outline-none'
          >
            <span className='text-sm text-thatch-800 underline'>
              Why am I being billed for more than employees' allowances?
            </span>
          </button>
        </ThatchTooltip>
      </div>

      {/** Table for desktop */}
      <div className='mt-4 hidden md:block'>
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.ColumnHeader
                onSort={() => handleSort('full_name')}
                sorted={getSortState('full_name')}
              >
                Full name
              </Table.ColumnHeader>
              {showPlanYear && (
                <Table.ColumnHeader
                  onSort={() => handleSort('plan_year')}
                  sorted={getSortState('plan_year')}
                >
                  Plan Year
                </Table.ColumnHeader>
              )}
              <Table.ColumnHeader
                align='right'
                onSort={() => handleSort('prefunding')}
                sorted={getSortState('prefunding')}
              >
                Pre-payment for {nextMonthName} insurance coverage
              </Table.ColumnHeader>
              <Table.ColumnHeader
                align='right'
                onSort={() => handleSort('premiums')}
                sorted={getSortState('premiums')}
              >
                Payment for {priorMonthName} insurance coverage
              </Table.ColumnHeader>
              <Table.ColumnHeader
                align='right'
                onSort={() => handleSort('coverage_corrections')}
                sorted={getSortState('coverage_corrections')}
              >
                Changes in {priorMonthName} insurance coverage
              </Table.ColumnHeader>
              <Table.ColumnHeader
                align='right'
                onSort={() => handleSort('other')}
                sorted={getSortState('other')}
              >
                Qualified medical expenses
              </Table.ColumnHeader>
              <Table.ColumnHeader
                align='right'
                onSort={() => handleSort('total')}
                sorted={getSortState('total')}
              >
                Total healthcare cost
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {paginatedData.map((memberInvoice) => (
              <Table.Row
                key={memberInvoice.id}
                onClick={() => onMemberClick(memberInvoice.id)}
                accessibleLabel={`View details for ${memberInvoice.full_name || 'member'}`}
              >
                <Table.Cell>
                  <span className='whitespace-nowrap'>
                    {memberInvoice.full_name || '---'}
                  </span>
                </Table.Cell>
                {showPlanYear && (
                  <Table.Cell>
                    <span className='inline-flex items-start'>
                      {memberInvoice.plan_year ?? '---'}
                    </span>
                  </Table.Cell>
                )}
                <Table.Cell align='right'>
                  <span className='inline-flex items-center justify-end'>
                    {formatMoneyValue(memberInvoice.amounts?.total_insurance_premiums)}
                    <span className='ml-1 w-4' aria-hidden />
                  </span>
                </Table.Cell>
                <Table.Cell align='right'>
                  <span className='inline-flex items-center justify-end'>
                    {formatMoneyValue(memberInvoice.amounts?.premiums)}
                    <span className='ml-1 w-4' aria-hidden />
                  </span>
                </Table.Cell>
                <Table.Cell align='right'>
                  <span className='inline-flex items-center justify-end'>
                    {formatMoneyValue(memberInvoice.amounts?.coverage_corrections)}
                    <span className='ml-1 w-4' aria-hidden />
                  </span>
                </Table.Cell>
                <Table.Cell align='right'>
                  <span className='inline-flex items-center justify-end'>
                    {formatMoneyValue(memberInvoice.amounts?.other)}
                    <span className='ml-1 w-4' aria-hidden />
                  </span>
                </Table.Cell>
                <Table.Cell align='right'>
                  <span className='inline-flex items-center justify-end'>
                    {formatMoneyValue(memberInvoice.amounts?.total)}
                    <span className='ml-1 w-4' aria-hidden />
                  </span>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>

      {/** Vertical layout for mobile */}
      <MemberInvoiceBreakdownMobile
        memberInvoices={paginatedData}
        priorMonthName={priorMonthName}
        nextMonthName={nextMonthName}
        formatMoneyValue={formatMoneyValue}
        showPlanYear={showPlanYear}
        onMemberClick={onMemberClick}
      />

      {totalPages > 1 && (
        <div className='mt-4 flex justify-end'>
          <Pagination
            pageIndex={currentPage}
            perPage={rowsPerPage}
            totalRows={sortedData.length}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  )
}
